import { useEffect, useState } from 'react'
import { theme } from '../theme'
import { api } from '../api'
import { groupUnitsForPicker } from '../units'
import { ModalShell } from './ModalShell'
import type { IngredientRow } from '../types'

type Props = {
  open: boolean
  seedName: string
  onCancel: () => void
  onCreated: (row: IngredientRow) => void
}

export function IngredientCreateModal({ open, seedName, onCancel, onCreated }: Props) {
  const [name, setName] = useState(seedName)
  const [unit, setUnit] = useState('ea')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset to the latest seed each time the modal opens so a stale name from
  // a previous invocation doesn't leak in.
  useEffect(() => {
    if (!open) return
    setName(seedName)
    setUnit('g')
    setNotes('')
    setError(null)
  }, [open, seedName])

  const save = async () => {
    setError(null)
    if (!name.trim()) { setError('Name required.'); return }
    setSaving(true)
    try {
      const row = await api.createIngredient({ name: name.trim(), default_unit: unit, notes: notes.trim() || undefined })
      onCreated(row)
    } catch (e) {
      setError(String(e))
    } finally {
      setSaving(false)
    }
  }

  const grouped = groupUnitsForPicker()

  return (
    <ModalShell
      open={open}
      onClose={onCancel}
      zIndex={60}
      autoHeight
      panelStyle={{ width: 'min(360px, 92%)', padding: '16px 18px', gap: 12 }}
    >
        <div>
          <div style={{
            fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 600,
            textTransform: 'uppercase', color: theme.muted,
          }}>New ingredient</div>
          <div style={{
            fontFamily: theme.serif, fontSize: 22, fontWeight: theme.headWeight,
            color: theme.ink, marginTop: 2,
          }}>Add to your library</div>
        </div>

        {error && (
          <div style={{
            padding: '8px 12px', borderRadius: theme.radius,
            background: 'rgba(184,83,57,0.08)', color: theme.accentInk,
            fontFamily: theme.sans, fontSize: 12,
          }}>{error}</div>
        )}

        <Field label="Name">
          <input autoFocus value={name} onChange={e => setName(e.target.value)} style={inputStyle}/>
        </Field>

        <Field label="Default unit">
          <select value={unit} onChange={e => setUnit(e.target.value)} style={{ ...inputStyle, appearance: 'auto' }}>
            <optgroup label="Mass">
              {grouped.mass.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
            </optgroup>
            <optgroup label="Volume">
              {grouped.volume.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
            </optgroup>
            <optgroup label="Count">
              {grouped.count.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
            </optgroup>
          </select>
        </Field>

        <Field label="Notes (optional)">
          <input value={notes} onChange={e => setNotes(e.target.value)} style={inputStyle}/>
        </Field>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <button onClick={onCancel} style={btnSecondary}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>
            {saving ? 'Saving…' : 'Create'}
          </button>
        </div>
    </ModalShell>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{
        fontFamily: theme.sans, fontSize: 10, letterSpacing: 1, fontWeight: 600,
        textTransform: 'uppercase', color: theme.muted,
      }}>{label}</span>
      {children}
    </label>
  )
}

const inputStyle: React.CSSProperties = {
  all: 'unset',
  boxSizing: 'border-box',
  width: '100%',
  padding: '7px 10px',
  borderRadius: theme.radius,
  border: `1px solid ${theme.lineStrong}`,
  background: theme.bg,
  fontFamily: theme.sans,
  fontSize: 13,
  color: theme.ink,
}

const btnSecondary: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '8px 14px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
  textTransform: 'uppercase', color: theme.ink,
  border: `1px solid ${theme.lineStrong}`,
}

const btnPrimary: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '8px 14px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
  textTransform: 'uppercase', color: theme.surface,
  background: theme.ink,
}
